import { BaseViewModel, IEntityConstructorOptions } from "./Base/BaseViewModel";
import { DailyEntryViewModel } from "./DailyEntryViewModel";
import { ItemsManager } from "./Services/ItemsManager";
import { GetCorrelation, ICorrelation } from "./Utilities";
import { IHeaderSetters as IHeaderSetters } from "../AppRoutes/Journal/Journal";
import { ISetHeaders } from "./ViewModelInterfaces";
import { JournalStrings } from "../AppRoutes/Journal/SharedComponents/JournalStrings";
import { RecapEntry, RecapEntryProperties, RecapEntryType, RecapEntryTypeOrder, RecapStatus } from "../Services/DataService/Model/RecapEntry";
import { format } from "date-fns";
import { CheckableTextData } from "../AppRoutes/Journal/SharedComponents/ModifiableText/CompletableText/CheckableTextGroup";
import { SimpleTextViewModel } from "./SimpleTextViewModel";
import { DataEntityBase, Models } from "../Services/DataService/Model";
import { ModelManager } from "./ModelManager";
import { EventHookManager, IConstructorOptions, IEventArgsBase, IProperyChangedArgs } from "../Utilities";
import { IDayStatus } from "../AppRoutes/Journal/SharedComponents/WeeklyProgress";
import { GetToday } from "../Services/Utilities";
import { BasicEntityWatcher } from "../Services/DataService/EntityWatcher";

export { RecapEntryType } from "../Services/DataService/Model/RecapEntry";


export enum PeriodType {
    Previous,
    Current
}

export class RecapEntriesViewModel extends BaseViewModel implements ISetHeaders {


    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    //
    // #region Static methods
    //
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


    public static get PropertyChanged() { return this._propertyChanged.eventHook; }
    private static _propertyChanged = new EventHookManager<IProperyChangedArgs>();
    public static get DisplayRecaps() {
        return this._displayRecaps;
    }


    private static updateRecaps = async () => {

        //
        // Steps to calculate the recap entries to show
        //
        // 1. Get the current date
        // 2. Get all the recaps that have been created in the last year
        // 3. Sort the recaps by end date with the most recent end date first
        // 4. pop the last recap off the list, then skip all recaps that have an end date after 
        //    the start date of the last recap
        // 5. Return the recaps that are left
        //

        //
        // Get all the completed recaps in the last year and sort them by type and then oldest to newest
        //
        const today = GetToday();
        const oneYearAgo = new Date(today);
        oneYearAgo.setFullYear(today.getFullYear() - 1);

        var recaps = await RecapEntriesViewModel.Manager.GetAllItems(GetCorrelation());
        recaps = recaps.filter((recap) => recap.TypedData.DateRange.end > oneYearAgo
            && recap.TypedData.status === RecapStatus.Complete);
        recaps = recaps.sort((a, b) => RecapEntriesViewModel.Compare(a, b));

        //
        // Now add the first recap to the retVal and then remove all recaps that have an end date after 
        // the start date of the first recap
        //
        var recapsToShow: RecapEntriesViewModel[] = [];
        while (recaps.length > 0) {
            var recap = recaps.shift()!;
            recapsToShow.push(recap);
            recaps = recaps.filter((r) => {
                return r.TypedData.DateRange.start > recap!.TypedData.DateRange.end;
            });
        }

        //
        // Reverse the list so that the most recent is first
        //
        let retVal = recapsToShow.reverse();

        //
        // Check to see if anything actually changed and if so, update and fire the event
        //
        let changed = false;
        if (this._displayRecaps.length !== retVal.length)
            changed = true;
        else
            for (let i = 0; i < this._displayRecaps.length; i++)
                if (this._displayRecaps[i] !== retVal[i])
                    changed = true;
        if (changed) {
            this._displayRecaps = retVal;
            let correlation = GetCorrelation();
            let hydratePromises = retVal.map((recap) => recap.Hydrate(correlation));
            await Promise.all(hydratePromises);
            this._propertyChanged.Fire({ property: 'Recaps', source: this });
        }


        return this._displayRecaps;
    }
    private static _displayRecaps: RecapEntriesViewModel[] = [];

    public static Initialize = async () => { await this.updateRecaps() };

    /**
     * The manager for the RecapEntriesViewModel objects.
     * 
     * @static
     * @memberof RecapEntriesViewModel
     * @type {IManageEntities<RecapEntriesViewModel>}
     * @public
     */
    public static Manager = new ModelManager<RecapEntriesViewModel>({
        createFN:
            async (options?: IEntityConstructorOptions) => {
                var builder = DataEntityBase.ConstructorOptionsBuilder(options);
                var myOptions = builder.construct();
                return new RecapEntriesViewModel(myOptions);
            },
        idFromKeyFN: (key: string) => RecapEntry.CreateId(key),
        typeName: Models.RecapEntry,
        onEntitiesChanged: RecapEntriesViewModel.updateRecaps
    });





    public static CompareTypes(a: RecapEntryType, b: RecapEntryType) {
        if (a === RecapEntryType.unknown || b === RecapEntryType.unknown)
            throw new Error(`Cannot compare ${a} to ${b}`);


        const indexA = RecapEntryTypeOrder.indexOf(a);
        const indexB = RecapEntryTypeOrder.indexOf(b);
        if (indexA === indexB) {
            return 0;
        }

        if (indexA > indexB) {
            return -1;
        }

        return 1;
    }

    public static Compare(a: RecapEntriesViewModel, b: RecapEntriesViewModel) {
        var typeCompare = RecapEntriesViewModel.CompareTypes(a.TypedData.entryType, b.TypedData.entryType);
        if (typeCompare !== 0)
            return typeCompare;
        var dif = a.TypedData.DateRange.end.getTime() - b.TypedData.DateRange.end.getTime();
        return dif;
    }





    public static GetNextHigherEntryType(currentType: RecapEntryType) {
        let currentIndex = RecapEntryTypeOrder.indexOf(currentType);
        if (currentIndex === RecapEntryTypeOrder.length - 1)
            return currentType;
        return RecapEntryTypeOrder[currentIndex + 1];
    };

    public static CreateId(key: string) {
        return DataEntityBase.CreateId(Models.RecapEntry, key);
    }

    public static CreateIdFromDate(date: Date, type: RecapEntryType) {
        return RecapEntriesViewModel.CreateId(RecapEntriesViewModel.CreateKey(date, type));
    }

    public static CreateKey(date: Date, type: RecapEntryType) {
        var periodDates = RecapEntriesViewModel.GetPeriodDates(type, date, PeriodType.Current);
        return RecapEntry.CreateKey(periodDates.end, type);
    }

    public static get RecapsChanged() {
        return RecapEntriesViewModel._recapChanged.eventHook;
    }


    public static GetPeriodDates = (type: RecapEntryType, date: Date, periodType: PeriodType) => {

        if (isNaN(date.getTime())) {
            throw new Error('Invalid date');
        }

        switch (type) {
            case RecapEntryType.Day:
                return { start: date, end: date };
            case RecapEntryType.Week:
                {
                    const daysToSubtract = date.getDay();
                    const mostRecentSunday = new Date(date);
                    mostRecentSunday.setDate(date.getDate() - daysToSubtract);
                    mostRecentSunday.setHours(0, 0, 0, 0);


                    const startOfReviewPeriod = new Date(mostRecentSunday);
                    startOfReviewPeriod.setDate(mostRecentSunday.getDate());
                    const endOfReviewPeriod = new Date(mostRecentSunday);
                    endOfReviewPeriod.setDate(mostRecentSunday.getDate() + 6);

                    if (periodType === PeriodType.Previous) {

                        startOfReviewPeriod.setDate(startOfReviewPeriod.getDate() - 7);
                        endOfReviewPeriod.setDate(endOfReviewPeriod.getDate() - 7);
                    }

                    return { start: startOfReviewPeriod, end: endOfReviewPeriod };

                }
            case RecapEntryType.Month:
                {
                    const mostRecentMonthStart = new Date(date.getFullYear(), date.getMonth(), 1); // Start of the current month

                    // Clone the date to avoid modifying the original date object
                    const endOfReviewPeriod = new Date(mostRecentMonthStart);
                    endOfReviewPeriod.setMonth(endOfReviewPeriod.getMonth() + 1); // Move to the next month
                    endOfReviewPeriod.setDate(0); // Set to the last day of the current month

                    if (periodType === PeriodType.Previous) {
                        const startPreviousPeriod = new Date(mostRecentMonthStart);
                        startPreviousPeriod.setMonth(startPreviousPeriod.getMonth() - 1); // Move to the previous month

                        const endPreviousPeriod = new Date(startPreviousPeriod);
                        endPreviousPeriod.setMonth(startPreviousPeriod.getMonth() + 1); // Move to the current month
                        endPreviousPeriod.setDate(0); // Last day of the previous month

                        return { start: startPreviousPeriod, end: endPreviousPeriod };
                    } else {
                        return { start: mostRecentMonthStart, end: endOfReviewPeriod };
                    }
                }
            case RecapEntryType.Quarter:
                {
                    const month = date.getMonth(); // 0 (Jan) to 11 (Dec)
                    const year = date.getFullYear();

                    // Calculate the current quarter (1-4)
                    const currentQuarter = Math.floor(month / 3) + 1;

                    // Calculate the start month of the current quarter
                    const startMonthOfCurrentQuarter = 3 * (currentQuarter - 1);

                    // Calculate the start and end dates for the current quarter
                    const startOfQuarter = new Date(year, startMonthOfCurrentQuarter, 1);
                    const endOfQuarter = new Date(year, startMonthOfCurrentQuarter + 3, 0); // The last day of the quarter

                    if (periodType === PeriodType.Previous) {
                        // Calculate the start and end dates for the previous quarter
                        const startOfPreviousQuarter = new Date(year, startMonthOfCurrentQuarter - 3, 1);
                        const endOfPreviousQuarter = new Date(year, startMonthOfCurrentQuarter, 0); // Last day of the previous quarter

                        return { start: startOfPreviousQuarter, end: endOfPreviousQuarter };
                    } else {

                        return { start: startOfQuarter, end: endOfQuarter };
                    }

                }
            case RecapEntryType.Year:
                {
                    const year = date.getFullYear();

                    // Calculate the start and end dates for the current year
                    const startOfYear = new Date(year, 0, 1); // January 1st of the current year
                    const endOfYear = new Date(year, 11, 31); // December 31st of the current year

                    if (periodType === PeriodType.Previous) {
                        // Calculate the start and end dates for the previous year
                        const startOfPreviousYear = new Date(year - 1, 0, 1); // January 1st of the previous year
                        const endOfPreviousYear = new Date(year - 1, 11, 31); // December 31st of the previous year

                        return { start: startOfPreviousYear, end: endOfPreviousYear };
                    } else {

                        return { start: startOfYear, end: endOfYear };
                    }

                }
        }
        throw new Error('Invalid RecapEntryType');
    }


    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    //
    // #endregion Static methods
    //
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    public static get TypeName() { return Models.RecapEntry; };
    public get TypedData() { return ((this.Data as unknown) as RecapEntry); }



    private doSetHeader(setters: IHeaderSetters, header: {
        Title: string;
        Subtitle: {
            Day: string;
            Week: string;
            Month: string;
            Quarter: string;
            Year: string;
        }
    }): void {
        var SubTitle = "";
        switch (this.TypedData.entryType) {
            case RecapEntryType.Day:
                SubTitle = header.Subtitle.Day;
                break;
            case RecapEntryType.Week:
                SubTitle = header.Subtitle.Week;
                break;
            case RecapEntryType.Month:
                SubTitle = header.Subtitle.Month;
                break;
            case RecapEntryType.Quarter:
                SubTitle = header.Subtitle.Quarter;
                break;
            case RecapEntryType.Year:
                SubTitle = header.Subtitle.Year;
                break;
        }

        setters.setSubtitle(SubTitle);
        setters.setHeaderTitle(header.Title);
        setters.setIsReadyToRender(true);
    }

    public SetEntryHeader(setters: IHeaderSetters) {
        this.doSetHeader(setters, JournalStrings.RecapEntries.Entry.Header);
    }

    /************************************************************************************
     * 
     * Related to the routes
     * 
     ************************************************************************************/
    /***************************************************************
    *
    * Route related functions
    *
    ****************************************************************/
    public get BaseEntryRoute() {
        return `/journal/recapEntry`;
    }

    public get EditRoute() {
        if (this.TypedData.entryType === RecapEntryType.Day)
            return DailyEntryViewModel.EditSummaryRoute(this.TypedData.DateRange.start);
        return `${this.BaseEntryRoute}/${this.key}`;
    }



    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    //
    // #region UI Specific properties / methods
    //
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////


    /************************************************************************************
     * 
     * Related to the recap card
     * 
     ************************************************************************************/
    /**
     * Retrieves the header text for the card.
     */
    public get RecapsSectionCardHeader() {
        switch (this.TypedData.entryType) {
            case RecapEntryType.Day:
                return format(this.TypedData.DateRange.start, 'EEEE, MMMM d yyyy');
            case RecapEntryType.Week:
                return `${format(this.TypedData.DateRange.start, 'MMMM d')} - ${format(this.TypedData.DateRange.end, 'd, yyyy')}`;
            case RecapEntryType.Month:
                return `${format(this.TypedData.DateRange.start, 'MMMM')} ${format(this.TypedData.DateRange.start, 'yyyy')}`;
            case RecapEntryType.Quarter:
                return `${format(this.TypedData.DateRange.start, 'MMMM')} - ${format(this.TypedData.DateRange.end, 'MMMM')} ${format(this.TypedData.DateRange.start, 'yyyy')}`;
            case RecapEntryType.Year:
                return format(this.TypedData.DateRange.start, 'yyyy');
        }
    }


    /************************************************************************************
     * 
     * Related to the recap entry experience
     * 
     ************************************************************************************/
    public get MagicMomentHeaderText() { return JournalStrings.RecapEntries.Entry.MagicMomentHeaderText; }
    public get MagicMoments() { return this.allMagicMoments; }

    public get TopAchievementsHeaderText() { return JournalStrings.RecapEntries.Entry.TopAchievementsHeaderText; }
    public get TopAchievements() { return this.allTopAchievements; }

    public get ChildRecaps() { return this.childRecaps; }

    public get DescriptionHeaderText() {
        switch (this.TypedData.entryType) {
            case RecapEntryType.Week:
                return JournalStrings.RecapEntries.Entry.DescriptionHeaderText.Week;
            case RecapEntryType.Month:
                return JournalStrings.RecapEntries.Entry.DescriptionHeaderText.Month;
            case RecapEntryType.Quarter:
                return JournalStrings.RecapEntries.Entry.DescriptionHeaderText.Quarter;
            case RecapEntryType.Year:
                return JournalStrings.RecapEntries.Entry.DescriptionHeaderText.Year;
        }
    }

    public get SummariesReviewHeaderText() {
        switch (this.TypedData.entryType) {
            case RecapEntryType.Week:
                return JournalStrings.RecapEntries.Entry.SummaryReviewHeaderText.Week;
            case RecapEntryType.Month:
                return JournalStrings.RecapEntries.Entry.SummaryReviewHeaderText.Month;
            case RecapEntryType.Quarter:
                return JournalStrings.RecapEntries.Entry.SummaryReviewHeaderText.Quarter;
            case RecapEntryType.Year:
        }
    }
    public get SummaryReviews() { return this.allRecapSummaries!; }

    public get isHighlightsSelected() {
        const MagicMoments = this.selectedMagicMoments.values.length > 0;
        const TopAchievements = this.selectedAccomplishments.values.length > 0;

        return MagicMoments && TopAchievements;
    }

    public get isCompletable() {
        return this.isHighlightsSelected && this.TypedData.entry.length > 0;
    }

    /************************************************************************************
     * 
     * Related to the recap entry page
     * 
     ************************************************************************************/
    public SetPageHeader(setters: IHeaderSetters) {
        this.doSetHeader(setters, JournalStrings.RecapEntries.Page.Header);
    }

    public get PageCardHeader() {
        switch (this.TypedData.entryType) {
            case RecapEntryType.Day:
                return format(this.TypedData.DateRange.start, 'EEEE, MMMM d yyyy');
            case RecapEntryType.Week:
                return `${format(this.TypedData.DateRange.start, 'MMMM d')} - ${format(this.TypedData.DateRange.end, 'd, yyyy')}`;
            case RecapEntryType.Month:
                return `${format(this.TypedData.DateRange.start, 'MMMM')} ${format(this.TypedData.DateRange.start, 'yyyy')}`;
            case RecapEntryType.Quarter:
                return `${format(this.TypedData.DateRange.start, 'MMMM')} - ${format(this.TypedData.DateRange.end, 'MMMM')} ${format(this.TypedData.DateRange.start, 'yyyy')}`;
            case RecapEntryType.Year:
                return format(this.TypedData.DateRange.start, 'yyyy');
        }
    }

    public get PageTopAccomplishmentsText() {
        return JournalStrings.RecapEntries.Page.TopAccomplishments;
    }

    public get PageTopMagicMomentsText() {
        return JournalStrings.RecapEntries.Page.TopMagicMoments;
    }

    public get PageDescriptionHeaderText() {
        return JournalStrings.RecapEntries.Page.DescriptionHeader;
    }



    /************************************************************************************
     * 
     * Related to the recap entry page
     * 
     ************************************************************************************/
    public get WeeklyStatus(): IDayStatus[] {
        if (this.TypedData.entryType !== RecapEntryType.Week)
            throw new Error('Invalid entry type');
        return this._weeklyStatus;
    }


    public get scoreColor() {
        //
        //  Scores range from 0 to 10.  Need to calculate which of the 5 colors based on that. 
        //  The score is an average of the children, so it can be a decimal.   n
        //
        let score = this.TypedData.score;
        if (score <= 2)
            return "red";
        if (score <= 4)
            return "orange";
        if (score <= 6)
            return "yellow";
        if (score <= 8)
            return "lightgreen";
        if (score <= 10)
            return "green";

        throw new Error('Invalid score');
    }

    public get score() {
        const decimalValue: number = this.TypedData.score;
        const roundedValue: number = Math.round(decimalValue * 10) / 10;
        const resultString: string = roundedValue.toFixed(1);
        return resultString;
    }



    private updateScore = () => {
        if (this.childRecaps.values.length === 0) {
            return;
        }
        let previousScore = this.TypedData.score;
        let scores = this.childRecaps.values.map((entry) => entry.TypedData.score);
        let combinedScore = scores.reduce((a, b) => a + b, 0);
        let tempScore = combinedScore / scores.length;

        if (tempScore === previousScore)
            return;
        this.TypedData.score = tempScore;
        this.FireChanged('score');
        this.FireChanged('scoreColor');
    }

    private updateWeeklyStatus = async () => {

        //
        // First remove the old subscriptions
        //
        for (let entry of this._dailyEntries) {
            entry.TypedData.PropertyChanged.Unsubscribe(this.updateWeeklyStatus);
        }

        // We are only doing a weekly status
        //
        if (this.TypedData.entryType !== RecapEntryType.Week)
            return;

        let periodDates = RecapEntriesViewModel.GetPeriodDates(RecapEntryType.Week, this.TypedData.DateRange.end, PeriodType.Current);

        const retVal: IDayStatus[] = [];
        for (let date = periodDates.start; date <= periodDates.end; date.setDate(date.getDate() + 1)) {
            let day = new Date(date);
            let recap = this.childRecaps.values.find((entry) => entry.TypedData.DateRange.end.getDate() === day.getDate());


            let dayStatus: { date: Date, status: string, scoreColor?: string } = {
                date: day,
                status: RecapStatus.NotReady
            };

            if (recap) {
                dayStatus.status = recap.TypedData.status;
                if (dayStatus.status === RecapStatus.Complete) {
                    dayStatus.scoreColor = recap.scoreColor;
                }
            }
            else {
                if (day > GetToday())
                    dayStatus.status = RecapStatus.NotReady;
                else {
                    let entry = await DailyEntryViewModel.Manager.GetItemByKey(GetCorrelation(), DailyEntryViewModel.CreateKey(day));

                    if (entry) {
                        dayStatus.status = RecapStatus.Inprogress;
                        entry.TypedData.PropertyChanged.Subscribe(this.updateWeeklyStatus);
                        this._dailyEntries.push(entry);
                    }
                    else {
                        dayStatus.status = RecapStatus.NotStarted;
                    }
                }
            }
            retVal.push(dayStatus);
        }
        this._weeklyStatus = retVal;
        this.FireChanged('WeeklyStatus');
    }
    private _weeklyStatus: IDayStatus[] = [];
    private _dailyEntries: DailyEntryViewModel[] = [];


    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    //
    // #endregion UI Specific properties / methods
    //
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////









    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    //
    // #region Protectected / Private methods
    //
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////






    /**
     * Updates the RecapEntriesViewModel object when the data changes.
     * 
     * @returns A promise that resolves when the operation is complete.
     * 
     * @private
     * @memberof RecapEntriesViewModel
     */
    private updateIfReady = () => {

        const today = new Date();
        if (today > this.TypedData.DateRange.end) {
            const filtered = this.childRecaps.values.filter((entry) => entry.TypedData.status === RecapStatus.Complete);
            if (this.TypedData.status === RecapStatus.NotReady && filtered.length > 0)
                this.TypedData.status = RecapStatus.NotStarted;
        }
    }



    /**
     * Handles the PropertyChanged event for the RecapEntriesViewModel object to detect when our status
     * has changed and we need to create the parent object and insert ourselves into.
     * 
     * @param e The event arguments.
     * 
     * @private
     * @memberof RecapEntriesViewModel
     * 
     */
    onStatusChanged = async (e: IProperyChangedArgs) => {
        if (
            (e.property === RecapEntryProperties.status || e.property === 'isHydrated')
            && this.TypedData.status !== RecapStatus.NotReady
        ) {

            var correlation = GetCorrelation();
            if (!this.parentRecap) {

                //
                // Prepare to get or create
                //
                let nextHigherType = RecapEntriesViewModel.GetNextHigherEntryType(this.TypedData.entryType);
                var periodDates = RecapEntriesViewModel.GetPeriodDates(nextHigherType, this.TypedData.DateRange.end, PeriodType.Current);

                //
                // Create the options
                //
                var optionsBuilder = RecapEntry.OptionsFromKey(this.ParentRecapKey);
                optionsBuilder.addProp(RecapEntryProperties.endDate, periodDates.end);
                optionsBuilder.addProp(RecapEntryProperties.startDate, periodDates.start);
                optionsBuilder.addProp(RecapEntryProperties.entryType, nextHigherType);
                var options = optionsBuilder.construct();

                //
                // Get or create the recap and hydrate it
                //
                this.parentRecap = await RecapEntriesViewModel.Manager.GetOrCreateByKey(correlation, this.ParentRecapKey, options);
                await this.parentRecap.HydratePromise;
            }

            if (!this.parentRecap)
                throw new Error('Parent recap not created');

            var some = this.parentRecap.childRecaps.values.filter((entry) => entry.key === this.key);
            if (some.length === 0) {
                this.parentRecap.childRecaps.AddItem(correlation, this);
                this.parentRecap.updateIfReady();
            }

        }
    }
    private parentRecap: RecapEntriesViewModel | undefined;

    private get ParentRecapKey() {

        let nextHigherType = RecapEntriesViewModel.GetNextHigherEntryType(this.TypedData.entryType);
        var periodDates = RecapEntriesViewModel.GetPeriodDates(nextHigherType, this.TypedData.DateRange.end, PeriodType.Current);
        return RecapEntry.CreateKey(periodDates.end, nextHigherType);
    }





    /**
     * Updates the RecapEntriesViewModel object from its children.
     * 
     * @returns A promise that resolves when the operation is complete.
     * 
     * @private
     * @memberof RecapEntriesViewModel
     */
    private UpdateRecapFromChildren = async () => {

        //
        // There should be no references to other objects in the Hydration.
        //
        const combineCTVArrays = (
            {
                getItemsProperty: getProperty,
                selectionManager
            }: {
                getItemsProperty: (entry: RecapEntriesViewModel) => SimpleTextViewModel[],
                selectionManager: ItemsManager<SimpleTextViewModel>
            }) => {

            var retVal: CheckableTextData[] = [];
            var reviewableTextDoubleArray = this.childRecaps.values.map(
                (entry) => getProperty(entry) as SimpleTextViewModel[]);

            var reviewableTextArray: SimpleTextViewModel[] = [];
            reviewableTextDoubleArray.forEach((entry) => {
                reviewableTextArray = [...reviewableTextArray, ...entry]
            });

            retVal = reviewableTextArray.map((entry) => {

                let retVal = {
                    key: entry.key,
                    text: entry.TypedData.text,
                    checked: selectionManager.Includes(entry.key),
                    onItemChecked: () => {
                        retVal.checked = !retVal.checked;
                        if (retVal.checked) {
                            if (!selectionManager.Includes(entry.key))
                                selectionManager.AddItem(GetCorrelation(), entry);
                        }
                        else {
                            selectionManager.RemoveItem(GetCorrelation(), entry);
                        }
                    }
                };
                return retVal;
            });


            return retVal;
            // return { values: retVal, PropertyChanged: (new EventHookManager<IProperyChangedArgs>()).eventHook };
        }

        var correlation = GetCorrelation();
        await Promise.all(this.childRecaps.values.map(async (entry) => {
            await entry.selectedMagicMoments.Hydrate(correlation);
            await entry.selectedAccomplishments.Hydrate(correlation);
        }));

        this.allMagicMoments = combineCTVArrays({
            getItemsProperty: (entry) => {
                return entry.selectedMagicMoments.values
            },
            selectionManager: this.selectedMagicMoments
        });
        this.allTopAchievements = combineCTVArrays({
            getItemsProperty: (entry) => {
                return entry.selectedAccomplishments.values
            },
            selectionManager: this.selectedAccomplishments
        });
        this.allRecapSummaries = this.childRecaps.values.map((entry) => ({ recap: entry.TypedData.entry, header: entry.GetFormattedHeader() }));
        this.allRecapSummaries = this.allRecapSummaries.filter((entry) => entry.recap !== "");

        //
        // These methods rely on being hydrated -- must call them after SetHydrated.
        //
        this.updateIfReady();
    }



    /**
     * Retrieves the formatted header for the RecapEntriesViewModel object.
     * 
     * @returns (string) The formatted header.
     */
    private GetFormattedHeader = () => {
        switch (this.TypedData.entryType) {
            case RecapEntryType.Day:
                return format(this.TypedData.DateRange.start, 'EEEE, MMMM d yyyy');
            case RecapEntryType.Week:
                return `${format(this.TypedData.DateRange.start, 'MMMM d')} - ${format(this.TypedData.DateRange.end, 'd, yyyy')}`;
            case RecapEntryType.Month:
                return `${format(this.TypedData.DateRange.start, 'MMMM')} ${format(this.TypedData.DateRange.start, 'yyyy')}`;
            case RecapEntryType.Quarter:
                return `${format(this.TypedData.DateRange.start, 'MMMM')} - ${format(this.TypedData.DateRange.end, 'MMMM')} ${format(this.TypedData.DateRange.start, 'yyyy')}`;
            case RecapEntryType.Year:
                return format(this.TypedData.DateRange.start, 'yyyy');
        }
        throw new Error('Invalid RecapEntryType');
    }




    public static ___testHook___constructor(options?: IEntityConstructorOptions) {
        return new RecapEntriesViewModel(options);
    }




    protected CreateData(watcher: BasicEntityWatcher, options?: IConstructorOptions) {
        return RecapEntry.Create({ watcher: watcher, options: options });
    }


    /**
     * Creates an instance of RecapEntriesViewModel.
     * 
     * @param [options] The options for the RecapEntriesViewModel object.
     * @param [options.dataEntity] The data entity to use for the RecapEntriesViewModel object (typeof: DataEntityBase).
     * @param [options.sourceObject] The source object to use for the RecapEntriesViewModel object. (typeof: object).
     * 
     * @memberof RecapEntriesViewModel
     * @private
     * @constructor
     */
    private constructor(options?: IEntityConstructorOptions) {
        super(options);


        this.selectedMagicMoments = new ItemsManager(
            {
                typeName: Models.SimpleText,
                idMGR:
                {
                    getKeys: () => { return this.TypedData.selectedMagicMomentIds },
                    setKeys: (value) => {
                        this.TypedData.selectedMagicMomentIds = value
                    }
                }
            }
        );

        this.selectedAccomplishments = new ItemsManager(
            {
                typeName: Models.SimpleText,
                idMGR:
                {
                    getKeys: () => { return this.TypedData.selectedAccomplishmentIds },
                    setKeys: (value) => {
                        this.TypedData.selectedAccomplishmentIds = value
                    }
                }
            }
        );

        this.childRecaps = new ItemsManager(
            {
                typeName: Models.RecapEntry,
                idMGR:
                {
                    getKeys: () => { return this.TypedData.childRecapIds },
                    setKeys: (value) => {
                        this.TypedData.childRecapIds = value
                    }
                }
            }
        );

        this.Hydrate(GetCorrelation()).then(() => {

            this.onStatusChanged({ property: RecapEntryProperties.status, source: this.TypedData });


            //
            // Subscribe to the PropertyChanged event on our data object
            //
            const doPropChanged = (e: IProperyChangedArgs) => {
                switch (e.property) {

                    case 'isHydrated': // intentionally fall through to the most robust update since anything could have changed
                    case RecapEntryProperties.status:
                        this.onStatusChanged(e);
                        RecapEntriesViewModel.updateRecaps();
                        RecapEntriesViewModel._recapChanged.Fire({ source: this });
                        break;
                    case 'DateRange':
                    case RecapEntryProperties.endDate:
                    case RecapEntryProperties.startDate:
                    case RecapEntryProperties.entryType:
                        RecapEntriesViewModel._recapChanged.Fire({ source: this });
                        break;
                    case 'Data':
                        this.TypedData.PropertyChanged.Subscribe(doPropChanged);
                        break;
                }
            }
            this.TypedData.PropertyChanged.Subscribe(doPropChanged);
            this.PropertyChanged.Subscribe((e) => {
                if (e.property === 'Data') {
                    this.TypedData.PropertyChanged.Subscribe(doPropChanged);
                }
            });





            //
            // Subscribe to the PropertyChanged event of the children
            //
            const doChildPropChanged = (e: IProperyChangedArgs) => {
                switch (e.property) {
                    case RecapEntryProperties.status:
                        this.updateWeeklyStatus();
                        break;

                    // all data was swapped out, need to do as much as possible to
                    // update downstream objects
                    //
                    case 'Data':
                        this.updateScore();
                        this.updateWeeklyStatus();
                        break;
                    case RecapEntryProperties.score:
                        this.updateScore();
                        this.updateWeeklyStatus();
                        break;
                    case 'DateRange':
                        this.updateWeeklyStatus();
                        break;
                    case RecapEntryProperties.selectedMagicMomentIds:
                        this.UpdateRecapFromChildren();
                        break;
                    case RecapEntryProperties.selectedAccomplishmentIds:
                        this.UpdateRecapFromChildren();
                        break;
                    case RecapEntryProperties.entry:
                        this.UpdateRecapFromChildren();
                        break;
                }
            };

            //
            // Resubscribe to the PropertyChanged event of the children whenver the children change.
            //
            var previousChildren = this.childRecaps.values;
            this.childRecaps.PropertyChanged.Subscribe((e) => {
                if (e.property === 'values' || e.property === 'isHydrated') {
                    //
                    // Update the subscription to the PropertyChanged event of the children
                    //
                    const itemsToUnregistere = previousChildren.filter((entry) => !this.childRecaps.values.includes(entry));
                    const itemsToRegister = this.childRecaps.values.filter((entry) => !previousChildren.includes(entry));
                    itemsToUnregistere.forEach((entry) => {
                        entry.TypedData.PropertyChanged.Unsubscribe(doChildPropChanged);
                    });
                    itemsToRegister.forEach((entry) => {
                        entry.TypedData.PropertyChanged.Subscribe(doChildPropChanged);
                    });
                    previousChildren = this.childRecaps.values;

                    // Update the score and weekly status
                    //
                    this.updateScore();
                    this.updateWeeklyStatus();
                }
            });

            if (this.TypedData.entryType !== RecapEntryType.Day)
                this.updateScore();

        });

    }

    /**
 * Hydrates the RecapEntriesViewModel object.
 * @param _correlation The correlation object to use for the operation
 * @returns A promise that resolves when the operation is complete.
 * @override
 * @protected
 * @memberof RecapEntriesViewModel
 */
    protected async doHydrate(_correlation: ICorrelation) {

        const weeklyStatusPromise = this.updateWeeklyStatus();
        const updateRecapFromChildrenPromise = this.UpdateRecapFromChildren();
        await Promise.all([weeklyStatusPromise, updateRecapFromChildrenPromise]);

    }

    protected onReplacedData(_oldData?: DataEntityBase, _newData?: DataEntityBase) {
        // Do nothing
    }




    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    //
    // #endregion Private methods
    //
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////




    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    //
    // #region private fields
    //
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    private static _recapChanged = new EventHookManager<IEventArgsBase>();
    private childRecaps: ItemsManager<RecapEntriesViewModel>;

    private allTopAchievements?: CheckableTextData[];
    private allMagicMoments?: CheckableTextData[];
    private allRecapSummaries?: { recap: string, header: string }[];

    public selectedMagicMoments: ItemsManager<SimpleTextViewModel>;
    public selectedAccomplishments: ItemsManager<SimpleTextViewModel>;

    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    //
    // #endregion private fields
    //
    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
}